import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ApartmentIcon from '@mui/icons-material/Apartment';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { Fragment, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useCurrentUser from 'src/hooks/use-current-user';
import { hasLaborTeamViewAccess, hasLaborValidationEditAccess, useScopeChecker } from 'src/rbac';
import {
  approvePayPeriodAction,
  getBuildingsOverviewAction,
  getLaborEntryHistoryAction,
  getLaborReportAction,
  getPayPeriodCyclesAction,
  resetApprovePayPeriodAction,
  resetBuildingsOverviewAction,
  resetLaborEntryHistoryAction,
  resetLaborEntryStatusAction,
  resetLaborReportAction,
  resetPayPeriodCyclesAction,
  updateLaborEntryStatusAction,
} from 'src/redux/actions/laborHourActions';
import { getAllTherapists } from 'src/redux/actions/therapistsAction';
import { getFacilityTodayYmd } from 'src/utils/convertDateToSelectedTimeZone';
import {
  type AuditEvent,
  type AuditKind,
  type EntrySource,
  type EntryStatus,
  type EntryType,
  type LaborEntry,
  type PayPeriod,
} from './labor-report-data';
import { humanizeEnum, mapAuditHistory, mapLaborReport } from './labor-report-mapper';

// The padlock reads as gold rather than taking the chip's own colour.
const LOCK_GOLD = '#E3A008';

const STATUS_CONFIG: Record<
  EntryStatus,
  { label: string; bg: string; color: string; lock?: boolean }
> = {
  approved: { label: 'Approved', bg: '#DEF7EC', color: '#03543F', lock: true },
  'not-approved': { label: 'Not approved', bg: '#FDF6B2', color: '#8E4B10' },
  pending: { label: 'Pending', bg: '#F1F5F9', color: '#475569' },
  deleted: { label: 'Deleted', bg: '#E2E8F0', color: '#475569' },
};

const PERIOD_STATUS_CHIP: Record<string, { label: string; bg: string; color: string }> = {
  OPEN: { label: 'OPEN', bg: '#FEE2E2', color: '#B91C1C' },
  PARTIALLY_APPROVED: { label: 'PARTIALLY APPROVED', bg: '#FDF6B2', color: '#8E4B10' },
  APPROVED: { label: 'APPROVED', bg: '#DEF7EC', color: '#03543F' },
};

const BUILDING_STATE_STYLE: Record<string, { bg: string; color: string }> = {
  NEED_ACTION: { bg: '#FDF6B2', color: '#8E4B10' },
  READY_TO_APPROVE: { bg: '#E0E7FF', color: '#4338CA' },
  APPROVED: { bg: '#DEF7EC', color: '#03543F' },
  NO_ENTRIES: { bg: '#F1F5F9', color: '#475569' },
};

const SOURCE_TONE = {
  edited: { bg: '#EEF2FF', color: '#4F46E5' },
  plain: { bg: '#F1F5F9', color: '#475569' },
};

const AUDIT_DOT_COLOR: Record<AuditKind, string> = {
  entered: '#6366F1',
  approved: '#12B76A',
  'not-approved': '#F04438',
};

const THERAPIST_PAGE_SIZE = 50;
const REPORT_PAGE_SIZE = 20;

const STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'ACTION_NEEDED', label: 'Action needed' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'NOT_APPROVED', label: 'Not approved' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'DELETED', label: 'Deleted' },
];

const PERIOD_LENGTH_DAYS: Record<string, number> = {
  ONE_WEEK: 7,
  WEEKLY: 7,
  TWO_WEEKS: 14,
  BIWEEKLY: 14,
  THREE_WEEKS: 21,
  FOUR_WEEKS: 28,
};

const periodLengthOf = (value: any): number => {
  const numeric = Number(value);
  if (numeric > 0) return numeric;
  return PERIOD_LENGTH_DAYS[String(value || '').toUpperCase()] || 14;
};

const NOT_APPROVED_REASONS = [
  'INCORRECT_TIME_IN',
  'INCORRECT_TIME_OUT',
  'INCORRECT_DATE',
  'OVERLAPPING_HOURS',
  'INCORRECT_LOCATION',
  'DUPLICATE_ENTRY',
  'DID_NOT_WORK',
  'INCORRECT_TIME_CODE',
];

const StatusBadge: FC<{ status: EntryStatus }> = ({ status }) => {
  const cfg = STATUS_CONFIG[status];
  return (
    <Chip
      size="small"
      icon={
        cfg.lock ? (
          <LockOutlinedIcon sx={{ fontSize: 14, color: `${LOCK_GOLD} !important` }} />
        ) : undefined
      }
      label={cfg.label}
      sx={{
        height: 22,
        fontSize: 12,
        fontWeight: 600,
        bgcolor: cfg.bg,
        color: cfg.color,
        borderRadius: '6px',
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
};

const SourceBadge: FC<{ source: EntrySource }> = ({ source }) => {
  const tone = source.edited ? SOURCE_TONE.edited : SOURCE_TONE.plain;
  return (
    <Chip
      size="small"
      label={source.label}
      sx={{
        height: 22,
        fontSize: 12,
        fontWeight: 600,
        bgcolor: tone.bg,
        color: tone.color,
        borderRadius: '6px',
        '& .MuiChip-label': { px: 1 },
      }}
    />
  );
};

const TYPE_ICON_COMPONENT = {
  building: ApartmentIcon,
  drive: DirectionsCarIcon,
  home: HomeOutlinedIcon,
  other: AccessTimeIcon,
};

const TypeCell: FC<{ type: EntryType }> = ({ type }) => {
  const Icon = TYPE_ICON_COMPONENT[type.icon];
  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
    >
      <Icon sx={{ fontSize: 16, color: '#64748B' }} />
      <Typography sx={{ fontSize: 13, color: '#334155' }}>{type.label}</Typography>
    </Stack>
  );
};

const RowActions: FC<{
  status: EntryStatus;
  periodApproved?: boolean;
  canWrite?: boolean;
  busy?: boolean;
  onApprove: () => void;
  onNotApprove: () => void;
  onUnapprove: () => void;
}> = ({ status, periodApproved, canWrite, busy, onApprove, onNotApprove, onUnapprove }) => {
  if (status === 'deleted') return null;
  if (periodApproved) {
    return (
      <Typography sx={{ fontSize: 13, color: '#94A3B8', whiteSpace: 'nowrap' }}>
        Period approved
      </Typography>
    );
  }
  // No approve permission, or the API says this report is not writable — the row is still
  // fully reviewable (status, notes, audit history), just not actionable.
  if (!canWrite) {
    return (
      <Typography sx={{ fontSize: 13, color: '#94A3B8', whiteSpace: 'nowrap' }}>
        Read-only
      </Typography>
    );
  }
  if (status === 'approved') {
    return (
      <Button
        variant="outlined"
        size="small"
        disabled={busy}
        onClick={onUnapprove}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          borderColor: '#CBD5E1',
          color: '#334155',
          '&:hover': { borderColor: '#94A3B8', bgcolor: '#F8FAFC' },
        }}
      >
        Unapprove
      </Button>
    );
  }
  if (status === 'not-approved') {
    return (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
      >
        <Button
          variant="contained"
          size="small"
          startIcon={<CheckIcon />}
          disabled={busy}
          onClick={onApprove}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: '#6366F1',
            '&:hover': { bgcolor: '#4F46E5' },
          }}
        >
          Approve
        </Button>
        <Typography sx={{ fontSize: 12, color: '#94A3B8', whiteSpace: 'nowrap' }}>
          awaiting correction
        </Typography>
      </Stack>
    );
  }
  return (
    <Stack
      direction="row"
      spacing={1}
    >
      <Button
        variant="contained"
        size="small"
        startIcon={<CheckIcon />}
        disabled={busy}
        onClick={onApprove}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          bgcolor: '#6366F1',
          '&:hover': { bgcolor: '#4F46E5' },
        }}
      >
        Approve
      </Button>
      <Button
        variant="outlined"
        size="small"
        disabled={busy}
        onClick={onNotApprove}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          borderColor: '#F59E0B',
          color: '#B45309',
          '&:hover': { borderColor: '#D97706', bgcolor: '#FFFBEB' },
        }}
      >
        Not approve
      </Button>
    </Stack>
  );
};

const AuditHistory: FC<{ events: AuditEvent[]; loading?: boolean }> = ({ events, loading }) => (
  <Box sx={{ py: 2, px: 3, bgcolor: '#F8FAFC' }}>
    <Typography
      sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', color: '#94A3B8', mb: 1.5 }}
    >
      AUDIT HISTORY — ORIGINAL AND MODIFIED VALUES, WHO, WHEN
    </Typography>
    {loading && (
      <Stack
        alignItems="center"
        sx={{ py: 1.5 }}
      >
        <CircularProgress size={20} />
      </Stack>
    )}
    {!loading && events?.length === 0 && (
      <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>No audit history recorded.</Typography>
    )}
    <Stack spacing={1.25}>
      {!loading &&
        events?.map((event, idx) => (
          <Box key={idx}>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: AUDIT_DOT_COLOR[event?.kind],
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
                {event?.text}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>{event?.time}</Typography>
            </Stack>
            {event?.reason && (
              <Box
                sx={{
                  ml: 3,
                  mt: 0.75,
                  display: 'inline-block',
                  bgcolor: '#FEFCE8',
                  border: '1px solid #FDE68A',
                  borderRadius: '6px',
                  px: 1.5,
                  py: 0.5,
                }}
              >
                <Typography sx={{ fontSize: 12, color: '#8E4B10' }}>
                  Reason: &ldquo;{event?.reason}&rdquo;
                </Typography>
              </Box>
            )}
          </Box>
        ))}
    </Stack>
  </Box>
);

const LaborReportAndApproval: FC = () => {
  const dispatch = useDispatch();
  const has = useScopeChecker();
  const [providerId] = useCurrentUser(['providerId']);
  const zoneId = localStorage.getItem('facility_timezone') || '';

  const canViewAllLabor = hasLaborTeamViewAccess(has);

  const laborReport = useSelector((state: any) => state.getLaborReportReducer);
  const loading = !!laborReport?.loading;

  const [selectedDate, setSelectedDate] = useState('');
  const [periodStartOverride, setPeriodStartOverride] = useState<string | null>(null);
  const [therapistId, setTherapistId] = useState('');
  const [page, setPage] = useState(0);
  const [filterStatus, setFilterStatus] = useState('');
  const facilityToday = useMemo(() => getFacilityTodayYmd(), []);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [unapproveTarget, setUnapproveTarget] = useState<LaborEntry | null>(null);
  const [notApproveTarget, setNotApproveTarget] = useState<LaborEntry | null>(null);
  const [notApproveReason, setNotApproveReason] = useState('');
  const [reopenOpen, setReopenOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveCertified, setApproveCertified] = useState(false);
  // set when a single building's pay period is being approved from the buildings list
  const [approveBuilding, setApproveBuilding] = useState<{
    facilityId: string;
    facilityName: string;
  } | null>(null);
  const [showBuildings, setShowBuildings] = useState(false);
  const [buildingSearch, setBuildingSearch] = useState('');
  const [buildingFilter, setBuildingFilter] = useState<string | null>(null);
  const [selectedBuildingIds, setSelectedBuildingIds] = useState<string[]>([]);

  const therapistList = useSelector((state: any) => state?.getAllTherapistsByRoleReducer);
  const therapistRows = therapistList?.rows;
  const therapistsLoading = !!therapistList?.loading;
  const therapistsIsLast = !!therapistList?.isLast;

  const [therapistPage, setTherapistPage] = useState(0);
  const [therapistSearch, setTherapistSearch] = useState('');
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>();

  const payPeriodCycles = useSelector((state: any) => state?.getPayPeriodCyclesReducer);
  const buildingsOverview = useSelector((state: any) => state?.getBuildingsOverviewReducer);

  const laborValidationStartDate = useSelector(
    (state: any) => state?.getLaborValidationToggleReducer?.response?.startDate
  );

  const laborValidationLength = useSelector(
    (state: any) => state?.getLaborValidationToggleReducer?.response?.length
  );

  const periodLength = periodLengthOf(laborValidationLength);

  const currentPeriodStart = useMemo(() => {
    const anchor = moment(laborValidationStartDate, 'YYYY-MM-DD');
    if (!laborValidationStartDate || !anchor.isValid()) return '';

    const periodsElapsed = Math.max(
      0,
      Math.floor(moment(facilityToday, 'YYYY-MM-DD').diff(anchor, 'days') / periodLength)
    );
    return anchor.add(periodsElapsed * periodLength, 'days').format('YYYY-MM-DD');
  }, [laborValidationStartDate, periodLength, facilityToday]);

  const periodStart = periodStartOverride ?? currentPeriodStart;
  const periodEnd = periodStart
    ? moment(periodStart, 'YYYY-MM-DD')
        .add(periodLength - 1, 'days')
        .format('YYYY-MM-DD')
    : '';

  const dateOptions = useMemo(() => {
    if (!periodStart) return [];
    return Array.from({ length: periodLength }, (_unused, index) => {
      const day = moment(periodStart, 'YYYY-MM-DD').add(index, 'days');
      return { value: day.format('YYYY-MM-DD'), label: day.format('ddd, MMM D') };
    });
  }, [periodStart, periodLength]);

  const therapistOptions = useMemo(
    () =>
      (Array.isArray(therapistRows) ? therapistRows : [])
        .map((provider: any) => {
          const name =
            provider?.name ||
            [provider?.firstName, provider?.lastName].filter(Boolean).join(' ') ||
            provider?.userName ||
            '';
          return {
            id: String(provider?.providerId || provider?.uuid || provider?.id || ''),
            label: [name, provider?.discipline].filter(Boolean).join(', '),
          };
        })
        .filter((option: any) => option?.id && option?.label),
    [therapistRows]
  );

  useEffect(() => {
    if (!canViewAllLabor) return;
    dispatch(
      getAllTherapists({
        roles: 'THERAPIST',
        page: therapistPage,
        pageSize: THERAPIST_PAGE_SIZE,
        ...(therapistSearch ? { searchString: therapistSearch } : {}),
      })
    );
  }, [dispatch, canViewAllLabor, therapistPage, therapistSearch]);

  useEffect(() => () => clearTimeout(searchTimeout.current), []);

  const handleTherapistSearch = (value: string, reason: string) => {
    if (reason === 'reset') return;
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setTherapistPage(0);
      setTherapistSearch(value.trim());
    }, 500);
  };

  const loadMoreTherapists = () => {
    if (!therapistsIsLast && !therapistsLoading) setTherapistPage((prev) => prev + 1);
  };

  const selectedTherapist =
    therapistOptions.find((option: any) => option?.id === therapistId) || null;

  const hasActiveFilters = !!therapistId || !!selectedDate || !!filterStatus;

  const clearFilters = () => {
    setTherapistId('');
    setSelectedDate('');
    setFilterStatus('');
    setTherapistSearch('');
    setTherapistPage(0);
    setPage(0);
  };

  const reportParams = useMemo(
    () => ({
      periodStartDate: periodStart,
      ...(selectedDate ? { filterDate: selectedDate } : {}),
      zoneId,
      providerId: therapistId || (canViewAllLabor ? null : providerId),
      ...(filterStatus ? { filterStatus } : {}),
      // several buildings go as repeated params: facilityIds=a&facilityIds=b
      ...(selectedBuildingIds.length ? { facilityIds: selectedBuildingIds } : {}),
      page,
      pageSize: REPORT_PAGE_SIZE,
    }),
    [
      periodStart,
      selectedDate,
      zoneId,
      providerId,
      canViewAllLabor,
      therapistId,
      filterStatus,
      selectedBuildingIds,
      page,
    ]
  );

  useEffect(() => {
    if (!reportParams.periodStartDate) return;
    dispatch(getLaborReportAction(reportParams));
  }, [dispatch, reportParams]);

  useEffect(() => {
    if (!periodStart) return;
    dispatch(getPayPeriodCyclesAction({ page: 0, pageSize: 20, date: periodStart }));
  }, [dispatch, periodStart]);

  useEffect(() => {
    if (!periodStart) return;
    // No "load more" control in the list yet — request a page large enough to hold every
    // building in one response rather than silently dropping rows past the default page size.
    dispatch(getBuildingsOverviewAction({ periodStartDate: periodStart, page: 0, pageSize: 200 }));
    // a status filter picked for the old period's counts doesn't carry meaning for the new one;
    // the building selection does, so it stays until the user changes or clears it
    setBuildingFilter(null);
  }, [dispatch, periodStart]);

  useEffect(
    () => () => {
      dispatch(resetLaborReportAction());
      dispatch(resetLaborEntryHistoryAction());
      dispatch(resetLaborEntryStatusAction());
      dispatch(resetPayPeriodCyclesAction());
      dispatch(resetApprovePayPeriodAction());
      dispatch(resetBuildingsOverviewAction());
    },
    [dispatch]
  );

  const period: PayPeriod = useMemo(
    () =>
      mapLaborReport(laborReport?.response, periodStart, zoneId, {
        startDate: periodStart,
        lengthDays: periodLength,
      }),
    [laborReport?.response, periodStart, periodLength, zoneId]
  );

  const buildingsData = buildingsOverview?.response;
  const buildingsLoading = !!buildingsOverview?.loading;
  // `buildings` is a paged response — only the current page's content is shown here.
  const buildingRows = useMemo(
    () => (Array.isArray(buildingsData?.buildings?.content) ? buildingsData.buildings.content : []),
    [buildingsData]
  );
  // Entries only carry facilityName (not facilityId), so when several buildings are combined
  // this is the only way to look up the write access of the specific building a row belongs to.
  const buildingWriteAccessByName = useMemo(() => {
    const map: Record<string, boolean> = {};
    buildingRows.forEach((building: any) => {
      if (building?.facilityName) map[building.facilityName] = !!building?.hasWriteAccess;
    });
    return map;
  }, [buildingRows]);

  const filteredBuildingRows = useMemo(() => {
    let rows = buildingRows;
    if (buildingFilter) {
      rows = rows.filter((building: any) => building?.state === buildingFilter);
    }
    const term = buildingSearch.trim().toLowerCase();
    if (term) {
      rows = rows.filter((building: any) =>
        String(building?.facilityName ?? '')
          .toLowerCase()
          .includes(term)
      );
    }
    return rows;
  }, [buildingRows, buildingSearch, buildingFilter]);

  const handleBuildingFilterClick = (state: string) => {
    setShowBuildings(true);
    const next = buildingFilter === state ? null : state;
    setBuildingFilter(next);
    // only NEED_ACTION maps to a report status; every other chip sends none
    setFilterStatus(next === 'NEED_ACTION' ? 'ACTION_NEEDED' : '');
    setPage(0);
  };

  const viewingBuildingIds: string[] = useMemo(
    () =>
      buildingRows
        .filter((building: any) => building?.viewing && building?.facilityId)
        .map((building: any) => building.facilityId),
    [buildingRows]
  );

  // No explicit selection means the grid shows the building being viewed, so that one reads as
  // checked — it is the baseline the toggles below build on.
  const effectiveSelectedIds: string[] = useMemo(
    () => (selectedBuildingIds.length ? selectedBuildingIds : viewingBuildingIds),
    [selectedBuildingIds, viewingBuildingIds]
  );

  // The bottom "Approve Pay Period" action approves for the viewing facility implicitly (no
  // facilityId is sent) — only show it when the selection resolves to that building, otherwise
  // it would silently approve a different building than the one on screen.
  const isViewingSelectionOnly =
    !selectedBuildingIds.length ||
    (selectedBuildingIds.length === viewingBuildingIds.length &&
      selectedBuildingIds.every((id) => viewingBuildingIds.includes(id)));

  const toggleBuildingSelection = (facilityId?: string) => {
    if (!facilityId) return;
    const base = effectiveSelectedIds;
    if (base.includes(facilityId)) {
      // at least one building must stay selected
      if (base.length <= 1) return;
      setSelectedBuildingIds(base.filter((id) => id !== facilityId));
    } else {
      setSelectedBuildingIds([...base, facilityId]);
    }
    setPage(0);
  };

  const handleSelectAllBuildings = () => {
    const visibleIds = filteredBuildingRows
      .map((building: any) => building?.facilityId)
      .filter(Boolean);
    setSelectedBuildingIds(Array.from(new Set([...effectiveSelectedIds, ...visibleIds])));
    setPage(0);
  };

  const handleClearAllBuildings = () => {
    setSelectedBuildingIds([]);
    setPage(0);
  };

  const handleToggleBuildingsList = () => {
    setShowBuildings((prev) => {
      const next = !prev;
      if (!next) setBuildingFilter(null);
      return next;
    });
  };

  const [entries, setEntries] = useState<LaborEntry[]>(period.entries);

  useEffect(() => {
    setEntries(period.entries);
    setExpanded({});
  }, [period]);

  const payPeriod = { ...period, entries };

  const cycleStatus = useMemo(() => {
    const cycles = Array.isArray(payPeriodCycles?.cycles) ? payPeriodCycles.cycles : [];
    if (!cycles?.length || !periodStart) return undefined;
    const anchor = moment(periodStart, 'YYYY-MM-DD');
    const match = cycles.find(
      (cycle: any) =>
        cycle?.startDate &&
        anchor.isBetween(
          moment(cycle.startDate, 'YYYY-MM-DD'),
          moment(cycle?.endDate || cycle.startDate, 'YYYY-MM-DD'),
          'day',
          '[]'
        )
    );
    return String((match || cycles[0])?.status || '').toUpperCase() || undefined;
  }, [payPeriodCycles?.cycles, periodStart]);

  // getPayPeriodCyclesAction always targets the viewing facility (no facilityId param), so
  // cycleStatus only applies while that's what's in view. For a single explicitly selected
  // building, the labor report's own status field doesn't reliably carry that building's
  // approval state either — the buildings-overview row (the source of the list's own chip) does,
  // so that's the source of truth once a specific building is in view.
  const selectedBuildingRow =
    !isViewingSelectionOnly && effectiveSelectedIds.length === 1
      ? buildingRows.find((building: any) => building?.facilityId === effectiveSelectedIds[0])
      : undefined;
  const periodStatus = isViewingSelectionOnly
    ? cycleStatus ?? payPeriod?.status
    : selectedBuildingRow?.state ?? payPeriod?.status;
  const isApproved = periodStatus === 'APPROVED';
  // The API's own write flag AND "Edit Labor Validation" — either one missing leaves the
  // table read-only, which is all "View Labor Validation" on its own grants.
  // facility only distinguishes rows when several buildings are combined
  const showFacilityColumn = effectiveSelectedIds?.length > 1;
  const canEditLaborValidation = hasLaborValidationEditAccess(has);
  // Same building-overview-row source as periodStatus above — the labor report's own
  // hasWriteAccess/showApprovedPayPeriod flags don't reliably reflect a non-viewing selected
  // building, but the row already gates its own inline Approve button on these same fields.
  const hasFacilityWriteAccess = isViewingSelectionOnly
    ? !!payPeriod?.hasWriteAccess
    : !!selectedBuildingRow?.hasWriteAccess;
  const canWrite = hasFacilityWriteAccess && canEditLaborValidation;
  // mirrors isViewingSelectionOnly's single-building case — the building this status/reopen/
  // approve action actually applies to when it isn't the viewing facility
  const contextFacilityId = isViewingSelectionOnly ? undefined : effectiveSelectedIds[0];

  const confirmReopen = () => {
    dispatch(
      approvePayPeriodAction({
        startDate: periodStart,
        endDate: periodEnd,
        reopen: true,
        ...(contextFacilityId ? { facilityId: contextFacilityId } : {}),
      })
    );
    setReopenOpen(false);
  };

  const approvePayPeriod = useSelector((state: any) => state?.approvePayPeriodReducer);

  const confirmApprovePeriod = () => {
    dispatch(
      approvePayPeriodAction({
        startDate: periodStart,
        endDate: periodEnd,
        reopen: false,
        ...(contextFacilityId ? { facilityId: contextFacilityId } : {}),
      })
    );
    setApproveOpen(false);
    setApproveCertified(false);
  };

  const closeApproveBuilding = () => {
    setApproveBuilding(null);
    setApproveCertified(false);
  };

  const confirmApproveBuilding = () => {
    if (!approveBuilding) return;
    dispatch(
      approvePayPeriodAction({
        startDate: periodStart,
        endDate: periodEnd,
        reopen: false,
        facilityId: approveBuilding.facilityId,
      })
    );
    closeApproveBuilding();
  };

  useEffect(() => {
    if (!approvePayPeriod?.isSuccess) return;
    dispatch(getPayPeriodCyclesAction({ page: 0, pageSize: 20, date: periodStart }));
    dispatch(getLaborReportAction(reportParams));
    dispatch(resetLaborEntryHistoryAction());
    dispatch(resetApprovePayPeriodAction());
  }, [dispatch, approvePayPeriod?.isSuccess, reportParams, periodStart]);

  const isFirstPeriod =
    !periodStart ||
    !laborValidationStartDate ||
    !moment(periodStart, 'YYYY-MM-DD').isAfter(moment(laborValidationStartDate, 'YYYY-MM-DD'));

  const goToPeriod = (delta: number) => {
    if (!periodStart || (delta < 0 && isFirstPeriod)) return;
    setSelectedDate('');
    setPage(0);
    setPeriodStartOverride(
      moment(periodStart, 'YYYY-MM-DD')
        .add(delta * periodLength, 'days')
        .format('YYYY-MM-DD')
    );
  };

  const historyById = useSelector((state: any) => state?.getLaborEntryHistoryReducer?.byId);

  const toggleRow = (id: string) => {
    const opening = !expanded[id];
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
    if (opening && !historyById?.[id]) {
      dispatch(getLaborEntryHistoryAction({ labourManagementId: id }));
    }
  };

  const statusUpdate = useSelector((state: any) => state?.updateLaborEntryStatusReducer);
  const pendingStatusById = statusUpdate?.pendingById;
  const statusUpdateError = statusUpdate?.error;

  const setEntryStatus = (id: string, status: string, reason?: string) =>
    dispatch(
      updateLaborEntryStatusAction({
        labourManagementId: id,
        status,
        ...(reason ? { reason } : {}),
      })
    );

  const lastSuccessId = statusUpdate?.lastSuccessId;
  useEffect(() => {
    if (!lastSuccessId) return;
    dispatch(getLaborReportAction(reportParams));
    dispatch(getPayPeriodCyclesAction({ page: 0, pageSize: 20, date: periodStart }));
    dispatch(getLaborEntryHistoryAction({ labourManagementId: lastSuccessId }));
    dispatch(resetLaborEntryStatusAction());
  }, [dispatch, lastSuccessId, reportParams, periodStart]);

  // The buildings roll-up (need action / ready / approved counts) can change whenever an
  // entry's status changes or the period itself is approved/reopened — refresh it either way,
  // success or failure, since a failed attempt can still be worth re-checking against.
  // periodStart lives in a ref, not the dependency array, so this only fires on a genuine
  // approve/status-update transition — the periodStart-only case is already handled above.
  const periodStartRef = useRef(periodStart);
  periodStartRef.current = periodStart;
  const isFirstBuildingsRefreshRef = useRef(true);
  useEffect(() => {
    if (isFirstBuildingsRefreshRef.current) {
      isFirstBuildingsRefreshRef.current = false;
      return;
    }
    if (!periodStartRef.current) return;
    dispatch(
      getBuildingsOverviewAction({
        periodStartDate: periodStartRef.current,
        page: 0,
        pageSize: 200,
      })
    );
  }, [
    dispatch,
    approvePayPeriod?.isSuccess,
    approvePayPeriod?.error,
    lastSuccessId,
    statusUpdateError,
  ]);

  const confirmUnapprove = () => {
    if (unapproveTarget) {
      setEntryStatus(unapproveTarget?.id, 'PENDING');
      setUnapproveTarget(null);
    }
  };

  const openNotApprove = (entry: LaborEntry) => {
    setNotApproveReason('');
    setNotApproveTarget(entry);
  };

  const confirmNotApprove = () => {
    if (!notApproveTarget || !notApproveReason) return;
    setEntryStatus(notApproveTarget?.id, 'NOT_APPROVED', notApproveReason);
    setNotApproveTarget(null);
  };

  // A single page cannot say how many entries remain period-wide — the server does.
  const pageInfo = payPeriod?.pagination;
  const therapistApproval = payPeriod?.therapistApproval;
  const therapistApprovalName = selectedTherapist?.label || entries?.[0]?.therapist?.name || '';
  const statusTone = therapistApproval?.fullyApproved
    ? { label: 'Fully approved', bg: '#DEF7EC', color: '#03543F', Icon: CheckIcon }
    : { label: 'Action needed', bg: '#FDF6B2', color: '#8E4B10', Icon: WarningAmberIcon };
  const therapistApprovalSummary = (() => {
    if (!therapistApproval) return '';
    const { totalCount, approvedCount, pendingCount, notApprovedCount, fullyApproved } =
      therapistApproval;
    const entryWord = totalCount === 1 ? 'entry' : 'entries';
    if (fullyApproved) {
      return `all ${totalCount} ${entryWord} in this pay period are approved`;
    }
    const parts = [`${approvedCount} of ${totalCount} ${entryWord} approved`];
    if (pendingCount) parts.push(`${pendingCount} pending`);
    if (notApprovedCount) parts.push(`${notApprovedCount} not approved`);
    // The roll-up spans the whole period, so say so — the table below is filtered.
    return `${parts.join(
      ' · '
    )} — counted across the full pay period, regardless of the date/status filters`;
  })();
  const remainingMessage = payPeriod?.remainingEntriesMessage;
  // THIN-7979: gate on the server's flag, never on the message. `remainingEntriesMessage`
  // is always populated — in the all-approved case it holds the "All entries approved"
  // prompt — so an emptiness check left the button permanently disabled. For a non-viewing
  // selected building, the labor report's showApprovedPayPeriod flag doesn't reliably reflect
  // it either, so fall back to the same server-driven readiness fields the row's own inline
  // Approve button gates on.
  const canApprovePeriod = isViewingSelectionOnly
    ? !!payPeriod?.showApprovedPayPeriod
    : selectedBuildingRow?.state === 'READY_TO_APPROVE' &&
      !!selectedBuildingRow?.allEntriesApproved;

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        flexWrap="wrap"
      >
        <Typography sx={{ fontSize: 24, fontWeight: 700, color: '#0F172A' }}>
          Labor Report &amp; Approval
        </Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#64748B' }}>Pay period</Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
        >
          <IconButton
            size="small"
            onClick={() => goToPeriod(-1)}
            disabled={loading || isFirstPeriod}
            sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', p: 0.5 }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0F172A' }}>
            {payPeriod?.label}
          </Typography>
          <IconButton
            size="small"
            onClick={() => goToPeriod(1)}
            disabled={loading || !periodStart}
            sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', p: 0.5 }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Stack>
        {effectiveSelectedIds?.length > 1 ? (
          // status is per building once several are combined, so the count replaces it
          <Chip
            label={`${effectiveSelectedIds.length} BUILDINGS`}
            size="small"
            sx={{
              height: 24,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.02em',
              borderRadius: '6px',
              bgcolor: '#E0E7FF',
              color: '#4338CA',
            }}
          />
        ) : (
          <Chip
            label={
              (PERIOD_STATUS_CHIP[periodStatus] ?? PERIOD_STATUS_CHIP.PARTIALLY_APPROVED).label
            }
            size="small"
            sx={{
              height: 24,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.02em',
              borderRadius: '6px',
              bgcolor: (PERIOD_STATUS_CHIP[periodStatus] ?? PERIOD_STATUS_CHIP.PARTIALLY_APPROVED)
                .bg,
              color: (PERIOD_STATUS_CHIP[periodStatus] ?? PERIOD_STATUS_CHIP.PARTIALLY_APPROVED)
                .color,
            }}
          />
        )}
      </Stack>

      {!isApproved && effectiveSelectedIds?.length > 1 && (
        <Typography sx={{ fontSize: 13, color: '#64748B', mt: 1.5 }}>
          Viewing{' '}
          <strong>
            {effectiveSelectedIds
              .map(
                (id) =>
                  buildingRows.find((building: any) => building?.facilityId === id)?.facilityName
              )
              .filter(Boolean)
              .join(' · ')}
          </strong>{' '}
          — one shared pay-period calendar. Period status and approval are{' '}
          <strong>per building</strong>: see the overview above, and approve each building from its
          row when it’s ready.
        </Typography>
      )}

      {!isApproved && effectiveSelectedIds?.length <= 1 && (
        <Typography sx={{ fontSize: 13, color: '#64748B', mt: 1.5 }}>
          {payPeriod?.cadence}
          {payPeriod?.weekdayRange && (
            <>
              , <strong>{payPeriod?.weekdayRange}</strong>
            </>
          )}
          .
          {payPeriod?.validationDue && (
            <>
              {' '}
              Validation due <strong>{payPeriod?.validationDue}</strong>.
            </>
          )}{' '}
          Every entry must be approved before the pay period can be approved for payroll.
        </Typography>
      )}

      {(buildingsLoading || buildingsData) && buildingsOverview?.response?.totalBuildings > 1 && (
        <Paper
          variant="outlined"
          sx={{
            mt: 2.5,
            px: 2,
            py: 1.25,
            border: '1px solid #E2E8F0',
            borderRadius: '10px',
            bgcolor: '#F8FAFC',
          }}
        >
          {buildingsLoading && !buildingsData ? (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
            >
              <CircularProgress size={16} />
              <Typography sx={{ fontSize: 13, color: '#64748B' }}>Loading buildings…</Typography>
            </Stack>
          ) : (
            <>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1.5}
                flexWrap="wrap"
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  flexWrap="wrap"
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      color: '#64748B',
                    }}
                  >
                    YOUR BUILDINGS ({buildingsData?.totalBuildings ?? 0}) ·{' '}
                    {payPeriod?.label?.toUpperCase?.() ?? ''}
                  </Typography>
                  <Chip
                    size="small"
                    label={`All statuses (${buildingsData?.totalBuildings ?? 0})`}
                    clickable
                    onClick={() => {
                      setShowBuildings(true);
                      setBuildingFilter(null);
                      setFilterStatus('');
                      setPage(0);
                    }}
                    sx={{
                      height: 28,
                      fontSize: 12,
                      fontWeight: 600,
                      borderRadius: '16px',
                      border: '1.5px solid',
                      borderColor: !buildingFilter ? '#6366F1' : '#E2E8F0',
                      color: !buildingFilter ? '#fff' : '#334155',
                      bgcolor: !buildingFilter ? '#6366F1' : '#fff',
                      '&:hover': { bgcolor: !buildingFilter ? '#4F46E5' : '#F1F5F9' },
                      '& .MuiChip-label': { px: 0.75 },
                      padding: 1,
                    }}
                  />
                  <Chip
                    size="small"
                    icon={
                      <WarningAmberIcon
                        sx={{
                          fontSize: 14,
                          color: `${
                            buildingsData?.needActionCount > 0 ? '#8E4B10' : '#334155'
                          } !important`,
                        }}
                      />
                    }
                    label={`${buildingsData?.needActionCount ?? 0} need action`}
                    variant={buildingsData?.needActionCount > 0 ? 'filled' : 'outlined'}
                    clickable
                    onClick={() => handleBuildingFilterClick('NEED_ACTION')}
                    sx={{
                      height: 28,
                      fontSize: 12,
                      fontWeight: 600,
                      borderRadius: '16px',
                      bgcolor: buildingsData?.needActionCount > 0 ? '#FDF6B2' : '#fff',
                      border: '1.5px solid',
                      borderColor: buildingFilter === 'NEED_ACTION' ? '#8E4B10' : '#E2E8F0',
                      color: buildingsData?.needActionCount > 0 ? '#8E4B10' : '#334155',
                      '& .MuiChip-label': { px: 0.75 },
                      padding: 1,
                    }}
                  />
                  <Chip
                    size="small"
                    label={`${buildingsData?.readyToApproveCount ?? 0} ready to approve`}
                    variant={buildingFilter === 'READY_TO_APPROVE' ? 'filled' : 'outlined'}
                    clickable
                    onClick={() => handleBuildingFilterClick('READY_TO_APPROVE')}
                    sx={{
                      height: 28,
                      fontSize: 12,
                      fontWeight: 600,
                      borderRadius: '16px',
                      border: '1.5px solid',
                      borderColor: buildingFilter === 'READY_TO_APPROVE' ? '#6366F1' : '#E2E8F0',
                      color: buildingFilter === 'READY_TO_APPROVE' ? '#4F46E5' : '#334155',
                      bgcolor: buildingFilter === 'READY_TO_APPROVE' ? '#EEF2FF' : '#fff',
                      '& .MuiChip-label': { px: 0.75 },
                      padding: 1,
                    }}
                  />
                  <Chip
                    size="small"
                    icon={
                      <CheckIcon
                        sx={{
                          fontSize: 14,
                          color: `${
                            buildingFilter === 'APPROVED' ? '#4F46E5' : '#334155'
                          } !important`,
                        }}
                      />
                    }
                    label={`${buildingsData?.approvedCount ?? 0} approved`}
                    variant="outlined"
                    clickable
                    onClick={() => handleBuildingFilterClick('APPROVED')}
                    sx={{
                      height: 28,
                      fontSize: 12,
                      fontWeight: 600,
                      borderRadius: '16px',
                      border: '1.5px solid',
                      borderColor: buildingFilter === 'APPROVED' ? '#6366F1' : '#E2E8F0',
                      color: buildingFilter === 'APPROVED' ? '#4F46E5' : '#334155',
                      bgcolor: buildingFilter === 'APPROVED' ? '#EEF2FF' : '#fff',
                      '& .MuiChip-label': { px: 0.75 },
                      padding: 1,
                    }}
                  />
                  {buildingsData?.noEntriesCount > 0 && (
                    <Chip
                      size="small"
                      label={`${buildingsData.noEntriesCount} no entries`}
                      variant="outlined"
                      clickable
                      onClick={() => handleBuildingFilterClick('NO_ENTRIES')}
                      sx={{
                        height: 28,
                        fontSize: 12,
                        fontWeight: 600,
                        borderRadius: '16px',
                        border: '1.5px solid',
                        borderColor: buildingFilter === 'NO_ENTRIES' ? '#6366F1' : '#E2E8F0',
                        color: buildingFilter === 'NO_ENTRIES' ? '#4F46E5' : '#334155',
                        bgcolor: buildingFilter === 'NO_ENTRIES' ? '#EEF2FF' : '#fff',
                        '& .MuiChip-label': { px: 0.75 },
                        padding: 1,
                      }}
                    />
                  )}
                </Stack>
                <Button
                  size="small"
                  onClick={handleToggleBuildingsList}
                  endIcon={
                    showBuildings ? (
                      <KeyboardArrowUpIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowDownIcon fontSize="small" />
                    )
                  }
                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: 13, color: '#4F46E5' }}
                >
                  {showBuildings ? 'Hide list' : 'Show buildings'}
                </Button>
              </Stack>
              <Collapse in={showBuildings}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  flexWrap="wrap"
                  sx={{ mt: 1.5 }}
                >
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search buildings…"
                    value={buildingSearch}
                    onChange={(e) => setBuildingSearch(e.target.value)}
                    sx={{
                      width: '25%',
                      minWidth: 200,
                      bgcolor: '#fff',
                      '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: '8px' },
                    }}
                  />
                  <Button
                    size="small"
                    onClick={handleSelectAllBuildings}
                    disabled={!filteredBuildingRows.length}
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, minWidth: 0 }}
                  >
                    Select all
                  </Button>
                  <Button
                    size="small"
                    onClick={handleClearAllBuildings}
                    disabled={!selectedBuildingIds.length}
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, minWidth: 0 }}
                  >
                    Clear all
                  </Button>
                  <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>
                    Check buildings to combine their punches in the table below — approve each
                    building from its row when it’s ready.
                  </Typography>
                </Stack>
                <Stack
                  spacing={0.5}
                  sx={{ mt: 1.5, maxHeight: 280, overflowY: 'auto', pr: 0.5 }}
                >
                  {filteredBuildingRows.length === 0 && (
                    <Typography sx={{ fontSize: 12, color: '#94A3B8', py: 1 }}>
                      No buildings match that search.
                    </Typography>
                  )}
                  {filteredBuildingRows?.map((building: any) => {
                    const stateStyle =
                      BUILDING_STATE_STYLE[building?.state] ?? BUILDING_STATE_STYLE.NO_ENTRIES;
                    const stateLabel =
                      building?.state === 'NEED_ACTION'
                        ? `${building?.toResolveCount ?? 0} to resolve`
                        : building?.state === 'READY_TO_APPROVE'
                        ? 'Ready to approve'
                        : building?.state === 'APPROVED'
                        ? 'Approved'
                        : 'No entries';
                    const isSelected =
                      !!building?.facilityId && effectiveSelectedIds.includes(building.facilityId);
                    return (
                      <Stack
                        key={building?.facilityId}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1.5}
                        onClick={() => toggleBuildingSelection(building?.facilityId)}
                        sx={{
                          px: 1.5,
                          py: 1,
                          cursor: 'pointer',
                          bgcolor: isSelected ? '#E0E7FF' : '#fff',
                          borderLeft: isSelected ? '3px solid #6366F1' : '3px solid transparent',
                          borderBottom: '1px solid #EEF2F6',
                          '&:hover': { bgcolor: isSelected ? '#E0E7FF' : '#F1F5F9' },
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                        >
                          <Checkbox
                            size="small"
                            checked={isSelected}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleBuildingSelection(building?.facilityId)}
                            sx={{ p: 0.5 }}
                          />
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>
                            {building?.facilityName}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1.5}
                        >
                          {building?.viewing && (
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#4F46E5' }}>
                              viewing
                            </Typography>
                          )}
                          {building?.state === 'READY_TO_APPROVE' &&
                            building?.allEntriesApproved &&
                            building?.hasWriteAccess &&
                            canEditLaborValidation && (
                              <Button
                                variant="contained"
                                size="small"
                                disabled={!!approvePayPeriod?.loading}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setApproveCertified(false);
                                  setApproveBuilding({
                                    facilityId: building?.facilityId,
                                    facilityName: building?.facilityName,
                                  });
                                }}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  fontSize: 12,
                                  // py: 0.25,
                                  bgcolor: '#6366F1',
                                  '&:hover': { bgcolor: '#4F46E5' },
                                  borderRadius: '8px',
                                }}
                              >
                                Approve
                              </Button>
                            )}
                          <Chip
                            size="small"
                            icon={
                              building?.state === 'NEED_ACTION' ? (
                                <WarningAmberIcon
                                  sx={{ fontSize: 13, color: `${stateStyle.color} !important` }}
                                />
                              ) : undefined
                            }
                            label={stateLabel}
                            sx={{
                              height: 24,
                              fontSize: 11,
                              fontWeight: 600,
                              borderRadius: '16px',
                              bgcolor: stateStyle.bg,
                              color: stateStyle.color,
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              </Collapse>
            </>
          )}
        </Paper>
      )}

      {isApproved && (
        <Paper
          variant="outlined"
          sx={{
            mt: 2.5,
            p: 2,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            bgcolor: '#ECFDF5',
            border: '1px solid #A7F3D0',
            borderRadius: '10px',
          }}
        >
          <TaskAltIcon sx={{ color: '#059669', fontSize: 22, mt: 0.25 }} />
          <Typography sx={{ fontSize: 13, color: '#065F46', flex: 1 }}>
            <strong>
              {isViewingSelectionOnly
                ? 'This pay period is approved.'
                : "This building's pay period is approved."}
            </strong>
            {payPeriod?.approvedBy && (
              <>
                {' '}
                Approved by {payPeriod?.approvedBy?.name}
                {payPeriod?.approvedBy?.role ? ` (${payPeriod?.approvedBy?.role})` : ''}
                {payPeriod?.approvedBy?.at ? ` · ${payPeriod?.approvedBy?.at}` : ''}.
              </>
            )}{' '}
            Approval is a status marker, not a lock — users with edit permission can still adjust or
            add entries, which returns the period to Partially Approved for re-approval. Therapists
            cannot change approved entries.
          </Typography>
          {canWrite && (
            <Button
              variant="outlined"
              size="small"
              disabled={!!approvePayPeriod?.loading}
              onClick={() => setReopenOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                borderColor: '#6366F1',
                color: '#4F46E5',
                bgcolor: '#fff',
              }}
            >
              Reopen Period
            </Button>
          )}
        </Paper>
      )}

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-end"
        spacing={2}
        flexWrap="wrap"
        sx={{ mt: 2.5 }}
      >
        <Box
          display={'flex'}
          gap={2}
        >
          {canViewAllLabor ? (
            <Stack spacing={0.75}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748B' }}>
                Therapist
              </Typography>
              <Autocomplete
                size="small"
                options={therapistOptions}
                value={selectedTherapist}
                loading={therapistsLoading}
                getOptionLabel={(option: any) => option?.label ?? ''}
                isOptionEqualToValue={(option: any, value: any) => option?.id === value?.id}
                filterOptions={(options) => options}
                onInputChange={(_event, value, reason) => handleTherapistSearch(value, reason)}
                onChange={(_event, option: any) => {
                  setTherapistId(option?.id || '');
                  setPage(0);
                }}
                noOptionsText={therapistsLoading ? 'Loading…' : 'No therapists found'}
                ListboxProps={{
                  onScroll: (event: React.UIEvent<HTMLUListElement>) => {
                    const node = event?.currentTarget;
                    if (Math.abs(node.scrollHeight - node.scrollTop - node.clientHeight) <= 1) {
                      loadMoreTherapists();
                    }
                  },
                }}
                sx={{
                  width: 240,
                  bgcolor: '#fff',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    fontSize: 13,
                    paddingTop: 0,
                    paddingBottom: 0,
                    paddingLeft: '8px',
                  },
                  // '& .MuiAutocomplete-input': { padding: '8.5px 4px !important' },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="All therapists"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {therapistsLoading ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Stack>
          ) : (
            <Box />
          )}

          <Stack spacing={0.75}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748B' }}>Date</Typography>
            <Select
              size="small"
              displayEmpty
              value={selectedDate}
              disabled={!dateOptions?.length}
              onChange={(event) => {
                setSelectedDate(event?.target.value);
                setPage(0);
              }}
              sx={{
                width: 200,
                bgcolor: '#fff',
                fontSize: 13,
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
              }}
            >
              <MenuItem
                value=""
                sx={{ fontSize: 13 }}
              >
                All dates
              </MenuItem>
              {dateOptions?.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{ fontSize: 13 }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Stack>

          <Stack spacing={0.75}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#64748B' }}>Status</Typography>
            <Select
              size="small"
              displayEmpty
              value={filterStatus}
              onChange={(event) => {
                setFilterStatus(event?.target.value);
                setPage(0);
              }}
              sx={{
                width: 200,
                bgcolor: '#fff',
                fontSize: 13,
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
              }}
            >
              {STATUS_FILTER_OPTIONS?.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  sx={{ fontSize: 13 }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Stack>

          {hasActiveFilters && (
            <Stack justifyContent="flex-end">
              <Button
                size="small"
                startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                onClick={clearFilters}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 13,
                  color: '#64748B',
                  alignSelf: 'flex-start',
                  '&:hover': { bgcolor: '#F1F5F9' },
                }}
              >
                Clear filters
              </Button>
            </Stack>
          )}
        </Box>
        {!isApproved && canWrite && effectiveSelectedIds?.length <= 1 ? (
          <Stack alignItems="flex-end">
            <Button
              variant="contained"
              startIcon={<CheckIcon />}
              disabled={!canApprovePeriod || !!approvePayPeriod?.loading}
              onClick={() => setApproveOpen(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#6366F1',
                '&:hover': { bgcolor: '#4F46E5' },
              }}
            >
              Approve Pay Period
            </Button>
            <Typography sx={{ fontSize: 12, color: '#94A3B8', mt: 0.75 }}>
              {remainingMessage}
            </Typography>
          </Stack>
        ) : (
          !isApproved && (
            <Typography sx={{ fontSize: 13, color: '#64748B' }}>
              Period approval is per building — approve each building from the overview above when
              it’s ready.
            </Typography>
          )
        )}
      </Stack>

      {(statusUpdateError || approvePayPeriod?.error) && (
        <Alert
          severity="error"
          sx={{ mt: 2, borderRadius: '10px' }}
        >
          Could not update the approval status. Please try again.
        </Alert>
      )}

      {therapistApproval && (
        <Paper
          variant="outlined"
          sx={{
            mt: 2.5,
            px: 2,
            py: 1.25,
            border: '1px solid #E2E8F0',
            borderRadius: '10px',
            bgcolor: '#FCFDFE',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            flexWrap="wrap"
          >
            {therapistApprovalName && (
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>
                {therapistApprovalName}
              </Typography>
            )}
            <Chip
              size="small"
              icon={
                <statusTone.Icon sx={{ fontSize: 14, color: `${statusTone.color} !important` }} />
              }
              label={statusTone.label}
              sx={{
                height: 22,
                fontSize: 12,
                fontWeight: 600,
                borderRadius: '6px',
                bgcolor: statusTone.bg,
                color: statusTone.color,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
            <Typography sx={{ fontSize: 13, color: '#64748B' }}>
              {therapistApprovalSummary}
            </Typography>
          </Stack>
        </Paper>
      )}

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ mt: 2.5, borderRadius: '12px', border: '1px solid #E2E8F0' }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ '& th': { bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' } }}>
              <TableCell sx={{ width: 48 }} />
              {[
                'DATE',
                ...(showFacilityColumn ? ['FACILITY'] : []),
                'THERAPIST',
                'TYPE',
                'LABOR CODE',
                'CLOCK IN',
                'CLOCK OUT',
                'TOTAL HOURS',
                'SOURCE',
                'STATUS',
                'ACTIONS',
              ].map((head) => (
                <TableCell
                  key={head}
                  sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', color: '#64748B' }}
                >
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell
                  colSpan={showFacilityColumn ? 12 : 11}
                  sx={{ borderBottom: 'none', py: 6 }}
                >
                  <Stack alignItems="center">
                    <CircularProgress size={28} />
                  </Stack>
                </TableCell>
              </TableRow>
            )}
            {!loading && entries?.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={showFacilityColumn ? 12 : 11}
                  sx={{ borderBottom: 'none', py: 6, textAlign: 'center' }}
                >
                  <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>
                    No labor entries in this pay period.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              entries?.map((entry) => (
                <Fragment key={entry?.id}>
                  <TableRow sx={{ '& td': { borderBottom: '1px solid #EEF2F6' } }}>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => toggleRow(entry?.id)}
                        sx={{ border: '1px solid #E2E8F0', borderRadius: '6px', p: 0.25 }}
                      >
                        {expanded[entry?.id] ? (
                          <KeyboardArrowUpIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowDownIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: 13, fontWeight: 600, color: '#1E293B', whiteSpace: 'nowrap' }}
                    >
                      {entry?.date}
                    </TableCell>
                    {showFacilityColumn && (
                      <TableCell sx={{ fontSize: 13, color: '#334155' }}>
                        {entry?.facilityName}
                      </TableCell>
                    )}
                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                      >
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: 11,
                            fontWeight: 700,
                            bgcolor: '#E2E8F0',
                            color: '#475569',
                          }}
                        >
                          {entry?.therapist?.initials}
                        </Avatar>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: '#1E293B',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {entry?.therapist?.name}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <TypeCell type={entry?.type} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, color: '#334155', whiteSpace: 'nowrap' }}>
                      {entry?.laborCode}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, color: '#334155', whiteSpace: 'nowrap' }}>
                      {entry?.clockIn}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, color: '#334155', whiteSpace: 'nowrap' }}>
                      {entry?.clockOut}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>
                      {entry?.totalHours}
                    </TableCell>
                    <TableCell>
                      <SourceBadge source={entry?.source} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={entry?.status} />
                      {entry?.statusNote && (
                        <Typography
                          sx={{ fontSize: 11, color: '#94A3B8', mt: 0.5, whiteSpace: 'nowrap' }}
                        >
                          {entry?.statusNote}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <RowActions
                        status={entry?.status}
                        periodApproved={isApproved}
                        canWrite={
                          showFacilityColumn
                            ? canEditLaborValidation &&
                              !!buildingWriteAccessByName[entry?.facilityName ?? '']
                            : canWrite
                        }
                        onApprove={() => setEntryStatus(entry?.id, 'APPROVED')}
                        busy={!!pendingStatusById?.[entry?.id]}
                        onNotApprove={() => openNotApprove(entry)}
                        onUnapprove={() => setUnapproveTarget(entry)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={showFacilityColumn ? 12 : 11}
                      sx={{
                        p: 0,
                        borderBottom: expanded[entry?.id] ? '1px solid #EEF2F6' : 'none',
                      }}
                    >
                      <Collapse
                        in={!!expanded[entry?.id]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <AuditHistory
                          events={
                            historyById?.[entry?.id]?.response
                              ? mapAuditHistory(historyById[entry?.id].response)
                              : entry?.auditHistory
                          }
                          loading={
                            !!historyById?.[entry?.id]?.loading && entry?.auditHistory?.length === 0
                          }
                        />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))}
          </TableBody>
        </Table>

        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={2}
          sx={{ px: 2, py: 1.5, borderTop: '1px solid #E2E8F0' }}
        >
          <Typography sx={{ fontSize: 13, color: '#64748B' }}>
            {entries?.length} of {pageInfo?.totalElements ?? 0} entries
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#64748B' }}>
            Page {(pageInfo?.page ?? 0) + 1} of {pageInfo?.totalPages ?? 1}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            disabled={loading || !!pageInfo?.first}
            onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
            sx={{ textTransform: 'none' }}
          >
            Previous
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={loading || !!pageInfo?.last}
            onClick={() => setPage((prev) => prev + 1)}
            sx={{ textTransform: 'none' }}
          >
            Next
          </Button>
        </Stack>
      </TableContainer>

      <Dialog
        open={!!unapproveTarget}
        onClose={() => setUnapproveTarget(null)}
        PaperProps={{ sx: { borderRadius: '14px', width: 460, maxWidth: '92vw' } }}
      >
        <DialogTitle sx={{ fontSize: 18, fontWeight: 700, color: '#0F172A', pb: 1.5 }}>
          Unapprove this entry?
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          {unapproveTarget && (
            <Box
              sx={{
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
                px: 1.5,
                py: 1.25,
                bgcolor: '#F8FAFC',
                mb: 2,
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
                {unapproveTarget?.date} · {unapproveTarget?.therapist?.name} ·{' '}
                {unapproveTarget?.type?.label} · {unapproveTarget?.totalHours} hrs
              </Typography>
            </Box>
          )}
          <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>
            The entry will be unlocked and return to <strong>Pending</strong>. It can be edited
            again until it is re-approved or the period is closed. This action is recorded in the
            audit trail.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={() => setUnapproveTarget(null)}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#334155' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmUnapprove}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#6366F1',
              '&:hover': { bgcolor: '#4F46E5' },
            }}
          >
            Unapprove Entry
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!notApproveTarget}
        onClose={() => setNotApproveTarget(null)}
        PaperProps={{ sx: { borderRadius: '14px', width: 480, maxWidth: '92vw' } }}
      >
        <DialogTitle sx={{ fontSize: 18, fontWeight: 700, color: '#0F172A', pb: 1.5 }}>
          Not approve this entry?
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          {notApproveTarget && (
            <Box
              sx={{
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
                px: 1.5,
                py: 1.25,
                bgcolor: '#F8FAFC',
                mb: 2,
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
                {notApproveTarget?.date} · {notApproveTarget?.therapist?.name} ·{' '}
                {notApproveTarget?.type?.label} · {notApproveTarget?.totalHours} hrs
              </Typography>
            </Box>
          )}
          <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.6, mb: 2 }}>
            The entry will be flagged for correction. It stays editable in Labor Hour Management
            until it is corrected and approved. The reason is recorded in the audit history and
            visible to the therapist?.
          </Typography>

          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>
            Reason{' '}
            <Box
              component="span"
              sx={{ color: '#EF4444' }}
            >
              *
            </Box>
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#94A3B8', mb: 1.5 }}>
            Predefined reasons — no free text.
          </Typography>
          <RadioGroup
            value={notApproveReason}
            onChange={(e) => setNotApproveReason(e.target.value)}
          >
            <Stack spacing={1}>
              {NOT_APPROVED_REASONS?.map((reason) => (
                <Box
                  key={reason}
                  onClick={() => setNotApproveReason(reason)}
                  sx={{
                    border: '1px solid',
                    borderColor: notApproveReason === reason ? '#6366F1' : '#E2E8F0',
                    bgcolor: notApproveReason === reason ? '#EEF2FF' : '#fff',
                    borderRadius: '8px',
                    px: 1.5,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  <FormControlLabel
                    value={reason}
                    control={<Radio size="small" />}
                    label={humanizeEnum(reason)}
                    sx={{
                      m: 0,
                      py: 0.5,
                      width: '100%',
                      '& .MuiFormControlLabel-label': { fontSize: 13, color: '#334155' },
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={() => setNotApproveTarget(null)}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#334155' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!notApproveReason}
            onClick={confirmNotApprove}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#6366F1',
              '&:hover': { bgcolor: '#4F46E5' },
            }}
          >
            Flag as Not Approved
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reopen an approved period back to Partially Approved */}
      <Dialog
        open={reopenOpen}
        onClose={() => setReopenOpen(false)}
        PaperProps={{ sx: { borderRadius: '14px', width: 480, maxWidth: '92vw' } }}
      >
        <DialogTitle sx={{ fontSize: 18, fontWeight: 700, color: '#0F172A', pb: 1.5 }}>
          Reopen Approved Period?
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.6, mb: 1.5 }}>
            Reopening returns this pay period to <strong>Partially Approved</strong> so entries can
            be added or adjusted — for example, labor you realized is missing. Individual entry
            approvals are not affected.
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.6, mb: 2 }}>
            All changes are recorded in the audit trail, and the period should be re-approved when
            you&apos;re done. Are you sure you want to reopen this period?
          </Typography>
          <Box
            sx={{
              border: '1px solid #E2E8F0',
              borderRadius: '8px',
              px: 1.5,
              py: 1.5,
              bgcolor: '#F8FAFC',
            }}
          >
            <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
              Requires the labor approval permission (DOR / RDO; also grantable to payroll admin).
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={() => setReopenOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#334155' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmReopen}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#6366F1',
              '&:hover': { bgcolor: '#4F46E5' },
            }}
          >
            Confirm &amp; Reopen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        PaperProps={{ sx: { borderRadius: '14px', width: 500, maxWidth: '92vw' } }}
      >
        <DialogTitle sx={{ fontSize: 18, fontWeight: 700, color: '#0F172A', pb: 1.5 }}>
          Approve Pay Period —{' '}
          {selectedBuildingRow?.facilityName ? `${selectedBuildingRow.facilityName} · ` : ''}
          {payPeriod?.label}
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.6, mb: 2 }}>
            Approving marks this pay period as reviewed and complete — your explicit statement that
            all labor has been validated and nothing is missing. It does not lock entries:
            authorized users can still make corrections, which return the period to Partially
            Approved for re-approval.
            {selectedBuildingRow?.facilityName && (
              <>
                {' '}
                Approval applies to <strong>this building only</strong> — every building approves
                its own pay period.
              </>
            )}
          </Typography>
          <Box
            sx={{
              border: '1px solid',
              borderColor: approveCertified ? '#6366F1' : '#E2E8F0',
              bgcolor: approveCertified ? '#EEF2FF' : '#F8FAFC',
              borderRadius: '8px',
              px: 1.5,
              py: 0.5,
              transition: 'all 0.15s',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={approveCertified}
                  onChange={(e) => setApproveCertified(e.target.checked)}
                />
              }
              label="I certify that I have reviewed and validated all labor entries associated with this pay period."
              sx={{
                m: 0,
                alignItems: 'flex-start',
                '& .MuiFormControlLabel-label': { fontSize: 13, color: '#334155', pt: 0.75 },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={() => setApproveOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#334155' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!approveCertified || !!approvePayPeriod?.loading}
            onClick={confirmApprovePeriod}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#6366F1',
              '&:hover': { bgcolor: '#4F46E5' },
            }}
          >
            Approve Pay Period
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!approveBuilding}
        onClose={closeApproveBuilding}
        PaperProps={{ sx: { borderRadius: '14px', width: 500, maxWidth: '92vw' } }}
      >
        <DialogTitle sx={{ fontSize: 18, fontWeight: 700, color: '#0F172A', pb: 1.5 }}>
          Approve Pay Period — {approveBuilding?.facilityName} · {payPeriod?.label}
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.6, mb: 2 }}>
            Approving marks this building&apos;s pay period as reviewed and complete — your explicit
            statement that all its labor has been validated and nothing is missing. It does not lock
            entries: authorized users can still make corrections, which return the period to
            Partially Approved for re-approval. Approval applies to{' '}
            <strong>this building only</strong> — every building approves its own pay period.
          </Typography>
          <Box
            sx={{
              border: '1px solid',
              borderColor: approveCertified ? '#6366F1' : '#E2E8F0',
              bgcolor: approveCertified ? '#EEF2FF' : '#F8FAFC',
              borderRadius: '8px',
              px: 1.5,
              py: 0.5,
              transition: 'all 0.15s',
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={approveCertified}
                  onChange={(e) => setApproveCertified(e.target.checked)}
                />
              }
              label="I certify that I have reviewed and validated all labor entries associated with this building's pay period."
              sx={{
                m: 0,
                alignItems: 'flex-start',
                '& .MuiFormControlLabel-label': { fontSize: 13, color: '#334155', pt: 0.75 },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={closeApproveBuilding}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#334155' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!approveCertified || !!approvePayPeriod?.loading}
            onClick={confirmApproveBuilding}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#6366F1',
              '&:hover': { bgcolor: '#4F46E5' },
            }}
          >
            Approve Pay Period
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LaborReportAndApproval;
